import React from 'react';
import { Line, Radar, Bar } from 'react-chartjs-2';
import { DashboardData, CalculatedData } from '../types';

interface ChartsProps {
  data: DashboardData;
  calculatedData: CalculatedData;
}

const Charts: React.FC<ChartsProps> = ({ data, calculatedData }) => {
  // チャートデータ
  const trendChartData = {
    labels: ['2020年', '2021年', '2022年', '2023年', '2024年', '2025年'],
    datasets: [
      {
        label: 'IT・通信業界採用倍率',
        data: [
          calculatedData.finalRatio * 0.75,
          calculatedData.finalRatio * 0.85,
          calculatedData.finalRatio * 0.94,
          calculatedData.finalRatio * 0.96,
          calculatedData.finalRatio * 0.98,
          calculatedData.finalRatio,
        ],
        borderColor: '#6a6ed5ff',
        backgroundColor: 'rgba(106, 110, 213, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const brandChartData = {
    labels: ['Brand Visibility', '認知度', '魅力度', '競争力', '総合評価'],
    datasets: [
      {
        label: '現在のスコア',
        data: [
          data.brandVisibility,
          Math.min(5.0, 2.5 + data.snsFollowers / 10000),
          Math.min(5.0, 3.0 + data.rating * 0.3),
          Math.min(5.0, 3.0 + data.prMeasures * 0.3),
          Math.min(
            5.0,
            (data.brandVisibility + data.rating + 3.0 + data.prMeasures * 0.3) /
              3
          ),
        ],
        backgroundColor: 'rgba(106, 110, 213, 0.2)',
        borderColor: '#6a6ed5ff',
        borderWidth: 2,
        pointBackgroundColor: '#6a6ed5ff',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
      },
    ],
  };

  const competitiveChartData = {
    labels: [
      'ブランド力',
      '給与水準',
      '成長性',
      '安定性',
      '働きやすさ',
      '技術力',
      '将来性',
    ],
    datasets: [
      {
        label: '自社ポジション',
        data: [
          Math.min(5.0, 2.5 + data.brandVisibility * 0.3),
          Math.min(5.0, 2.0 + (data.salary - 20) * 0.2),
          Math.min(5.0, data.growth),
          Math.min(5.0, data.stability),
          Math.min(5.0, data.workLifeBalance),
          Math.min(5.0, data.technicalCapability),
          Math.min(5.0, data.stability * 0.95),
        ],
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        borderColor: '#22c55e',
        borderWidth: 2,
        pointBackgroundColor: '#22c55e',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
      },
    ],
  };

  // 採用ファネル計算
  const totalHiring = data.generalHiring + data.techHiring;

  // ファネル各段階の計算
  const applicants = calculatedData.requiredApplicants; // 応募
  // 応募→書類選考：90%（固定）
  const documentScreening = Math.ceil(applicants * 0.9);

  // 目標から逆算：内定承諾 = totalHiring（確定）
  const calculatedJoining = totalHiring;
  // 内定 = 内定承諾 / 0.6
  const offers = Math.ceil(calculatedJoining / 0.6);
  // 面接 = 内定 / 0.2 = totalHiring / 0.12
  let interviews = Math.ceil(offers / 0.2);

  // 面接 > 書類選考の場合のみ、面接を書類選考に制限（内定・内定承諾は変更しない）
  if (interviews > documentScreening) {
    interviews = documentScreening;
  }

  const funnelChartData = {
    labels: ['母集団', '応募', '書類選考', '面接', '内定', '内定承諾'],
    datasets: [
      {
        label: '人数',
        data: [
          applicants * 1.5, // 母集団
          applicants, // 応募
          documentScreening, // 書類選考通過
          interviews, // 面接通過
          offers, // 内定
          calculatedJoining, // 内定承諾（計算値）
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
        ],
        borderColor: [
          '#3b82f6',
          '#10b981',
          '#f59e0b',
          '#ef4444',
          '#8b5cf6',
          '#22c55e',
        ],
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="lg:col-span-3">
      <div className="dashboard-card p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">分析結果</h2>

        {/* 採用倍率推移チャート */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            📈 採用倍率推移チャート
          </h3>
          <div className="chart-container">
            <Line
              data={trendChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: '採用倍率（倍）',
                    },
                  },
                  x: {
                    title: {
                      display: true,
                      text: '年',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* ブランドスコア5項目分析 */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            🌟 ブランドスコア5項目分析
          </h3>
          <div className="chart-container">
            <Radar
              data={brandChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                  },
                },
                scales: {
                  r: {
                    angleLines: {
                      display: true,
                    },
                    suggestedMin: 0,
                    suggestedMax: 5,
                    ticks: {
                      stepSize: 1,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* 競合ポジション7要素分析 */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            🏆 競合ポジション7要素分析
          </h3>
          <div className="chart-container">
            <Radar
              data={competitiveChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                  },
                },
                scales: {
                  r: {
                    angleLines: {
                      display: true,
                    },
                    suggestedMin: 0,
                    suggestedMax: 5,
                    ticks: {
                      stepSize: 1,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* 採用ファネル分析 */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            📊 採用ファネル分析
          </h3>
          <div className="chart-container">
            <Bar
              data={funnelChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: '人数',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charts;
